import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Products from '../components/Products';

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [scrollProgress, setScrollProgress] = useState(0);
  
  // Refs for sections
  const servicesRef = useRef(null);
  const aboutRef = useRef(null);
  const contactRef = useRef(null);
  const [isServicesVisible, setIsServicesVisible] = useState(false);
  const [isAboutVisible, setIsAboutVisible] = useState(false);
  const [isContactVisible, setIsContactVisible] = useState(false);

  // Scroll progress tracker
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection Observer for animations
  useEffect(() => {
    const options = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.id;
          if (id === 'services') setIsServicesVisible(true);
          if (id === 'about') setIsAboutVisible(true);
          if (id === 'contact') setIsContactVisible(true);
        }
      });
    }, options);

    // Observe each section
    if (servicesRef.current) observer.observe(servicesRef.current);
    if (aboutRef.current) observer.observe(aboutRef.current);
    if (contactRef.current) observer.observe(contactRef.current);

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('Please enter your full name');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert('Please enter a valid email address');
      return;
    }

    if (!formData.message.trim()) {
      alert('Please enter your message');
      return;
    }

    setIsSending(true);

    try {
      const response = await fetch(
        'https://salstechitsolutions.com/api/send-contact-email.php',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        }
      );

      const result = await response.json();

      if (result.success) {
        alert(
          '✅ Message sent successfully! Check your email for confirmation. We\'ll get back to you within 24 hours.'
        );
        setFormData({ name: '', email: '', message: '' });
      } else {
        alert('❌ Sorry, there was an error: ' + (result.error || 'Please try again'));
      }
    } catch (error) {
      alert('❌ Network error. Please check your connection and try again.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Scroll Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 z-50">
        <div
          className="h-full bg-gradient-to-r from-sals-primary to-sals-light transition-all duration-300"
          style={{ width: `${scrollProgress}%` }}
        ></div>
      </div>

      <Navbar scrollToSection={scrollToSection} />

      {/* Hero Section with Background Image */}
      <section
        id="home"
        className="relative pt-16 md:pt-20 min-h-screen flex items-center overflow-hidden"
      >
        {/* Animated Background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-sals-dark via-sals-primary to-sals-light opacity-95"></div>
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3')] bg-cover bg-center opacity-60"></div>
          {/* Floating particles */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full bg-white/10 animate-float"
                style={{
                  width: Math.random() * 6 + 2 + 'px',
                  height: Math.random() * 6 + 2 + 'px',
                  left: Math.random() * 100 + '%',
                  top: Math.random() * 100 + '%',
                  animationDelay: Math.random() * 5 + 's',
                  animationDuration: Math.random() * 10 + 10 + 's',
                }}
              ></div>
            ))}
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl mx-auto text-center">
            <div className="text-white">
              <div className="mb-4 inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm animate-pulse-slow">
                🇳🇬 Nigeria's Trusted Tech Partner
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight animate-slide-up">
                Building Digital <span className="text-sals-light">Excellence</span>
              </h1>
              <p className="text-lg md:text-xl mb-8 opacity-90 leading-relaxed animate-slide-up" style={{animationDelay: '200ms'}}>
                SalsTech IT Solutions delivers innovative software, web applications, and
                professional web development training. Let's bring your ideas to life.
              </p>
              <div className="flex flex-wrap gap-4 justify-center animate-slide-up" style={{animationDelay: '400ms'}}>
                <button
                  onClick={() => scrollToSection('services')}
                  className="group bg-white text-sals-dark px-8 py-3 rounded-lg font-semibold hover:bg-sals-light hover:text-white transition-all duration-300 transform hover:scale-105 shadow-lg relative overflow-hidden"
                >
                  <span className="relative z-10">Explore Services</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-sals-primary to-sals-light transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-sals-dark transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
                >
                  Contact Us
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-2 bg-white rounded-full mt-2 animate-scroll"></div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section 
        id="services" 
        ref={servicesRef}
        className={`py-20 bg-gray-50 transition-all duration-1000 ${
          isServicesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-sals-dark mb-4">
              Our <span className="text-sals-primary">Services</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-sals-primary to-sals-light mx-auto rounded-full"></div>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Comprehensive tech solutions tailored to your business needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'Software Development',
                description: 'Custom enterprise software solutions built with cutting-edge technology.',
                icon: '💻',
                color: 'from-sals-dark to-sals-primary',
                features: ['Custom Solutions', 'Scalable Architecture', 'API Integration'],
              },
              {
                title: 'Web Applications',
                description: 'Dynamic, scalable web apps that drive business growth.',
                icon: '🌐',
                color: 'from-sals-primary to-sals-light',
                features: ['Responsive Design', 'Real-time Features', 'Cloud Ready'],
              },
              {
                title: 'Websites',
                description: 'Responsive, SEO-friendly websites that make a lasting impression.',
                icon: '🎨',
                color: 'from-sals-light to-sals-primary',
                features: ['SEO Optimized', 'Mobile First', 'Fast Loading'],
              },
              {
                title: 'Web Dev Training',
                description: 'Comprehensive training for aspiring developers.',
                icon: '📚',
                color: 'from-sals-dark to-sals-light',
                features: ['Hands-on Projects', 'Expert Mentors', 'Job Ready'],
              },
            ].map((service, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl shadow-lg overflow-hidden transform hover:-translate-y-2 transition-all duration-300 group ${
                  isServicesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div
                  className={`bg-gradient-to-r ${service.color} p-6 text-center relative overflow-hidden`}
                >
                  <div className="relative z-10">
                    <div className="text-5xl mb-3 group-hover:scale-110 transition-transform duration-300 group-hover:rotate-12">
                      {service.icon}
                    </div>
                    <h3 className="text-xl font-bold text-white">{service.title}</h3>
                  </div>
                  <div className="absolute inset-0 bg-white/10 transform -skew-x-12 translate-x-full group-hover:translate-x-0 transition-transform duration-500"></div>
                  <div className="absolute -top-10 -right-10 w-20 h-20 bg-white/5 rounded-full blur-2xl"></div>
                </div>
                <div className="p-6">
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600 group-hover:translate-x-1 transition-transform duration-300">
                        <svg
                          className="w-4 h-4 text-sals-primary mr-2 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <Products />

      {/* About Section */}
      <section 
        id="about" 
        ref={aboutRef}
        className={`py-20 bg-white transition-all duration-1000 ${
          isAboutVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-sals-dark mb-6">
                About <span className="text-sals-primary">SalsTech</span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-sals-primary to-sals-light mb-6 rounded-full"></div>
              <p className="text-gray-700 mb-4 leading-relaxed">
                SalsTech IT Solutions is a premier technology company registered with the
                Corporate Affairs Commission (CAC) of Nigeria. We specialize in building
                innovative software solutions, web applications, and professional websites that
                drive business success.
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Our commitment to excellence and continuous learning has made us a trusted partner
                for businesses across Nigeria. We also empower the next generation of developers
                through our comprehensive web development training programs.
              </p>
              <div className="flex items-center space-x-2 text-sals-primary mb-4">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-semibold">Registered Company - RC: 7398490</span>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-sals-dark to-sals-primary rounded-2xl p-8 text-white transform hover:scale-105 transition-transform duration-300 shadow-2xl relative overflow-hidden">
                <div className="absolute inset-0 bg-white/5 transform rotate-45 translate-x-full animate-shimmer"></div>
                <h3 className="text-2xl font-bold mb-4">Why Choose Us?</h3>
                <ul className="space-y-3">
                  {[
                    '✅ Expert Team of Developers',
                    '✅ Cutting-Edge Technologies',
                    '✅ Timely Project Delivery',
                    '✅ 24/7 Customer Support',
                    '✅ Affordable & Transparent Pricing',
                    '✅ Post-Launch Maintenance',
                  ].map((item, i) => (
                    <li key={i} className="flex items-center hover:translate-x-2 transition-transform duration-300">
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-sals-light rounded-full opacity-20 blur-2xl animate-pulse-slow"></div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-sals-primary rounded-full opacity-20 blur-2xl animate-pulse-slow" style={{animationDelay: '1s'}}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section 
        id="contact" 
        ref={contactRef}
        className={`py-20 bg-gray-50 transition-all duration-1000 ${
          isContactVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-sals-dark mb-4">
              Get In <span className="text-sals-primary">Touch</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-sals-primary to-sals-light mx-auto rounded-full"></div>
            <p className="text-gray-600 mt-4">Let's discuss your next project or training needs</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div className={`bg-white rounded-2xl shadow-xl p-8 transition-all duration-1000 transform hover:shadow-2xl ${
              isContactVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="group">
                  <label className="block text-gray-700 font-medium mb-2">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    disabled={isSending}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sals-primary focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed group-hover:border-sals-primary"
                    placeholder="John Doe"
                  />
                </div>
                <div className="group">
                  <label className="block text-gray-700 font-medium mb-2">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sals-primary focus:border-transparent transition-all group-hover:border-sals-primary"
                    placeholder="john@example.com"
                  />
                </div>
                <div className="group">
                  <label className="block text-gray-700 font-medium mb-2">Message</label>
                  <textarea
                    rows="5"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sals-primary focus:border-transparent transition-all group-hover:border-sals-primary"
                    placeholder="Tell us about your project..."
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={isSending}
                  className="w-full bg-gradient-to-r from-sals-primary to-sals-light text-white py-3 rounded-lg font-semibold hover:from-sals-dark hover:to-sals-primary transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 relative overflow-hidden group"
                >
                  <span className="relative z-10">
                    {isSending ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg
                          className="animate-spin h-5 w-5"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      'Send Message'
                    )}
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-sals-light to-sals-primary transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-right"></div>
                </button>
              </form>
            </div>

            <div className={`space-y-6 transition-all duration-1000 ${
              isContactVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`} style={{ transitionDelay: '200ms' }}>
              <div className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-sals-dark mb-6">Contact Information</h3>
                <div className="space-y-6">
                  <div className="flex items-center space-x-4 group cursor-pointer">
                    <div className="w-12 h-12 bg-sals-light/20 rounded-full flex items-center justify-center group-hover:bg-sals-primary transition-colors duration-300 group-hover:scale-110 transform">
                      <svg
                        className="w-6 h-6 text-sals-primary group-hover:text-white transition-colors duration-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Visit Us</p>
                      <p className="text-gray-600">Elegushi Modern Market, Lagos, Nigeria</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 group cursor-pointer">
                    <div className="w-12 h-12 bg-sals-light/20 rounded-full flex items-center justify-center group-hover:bg-sals-primary transition-colors duration-300 group-hover:scale-110 transform">
                      <svg
                        className="w-6 h-6 text-sals-primary group-hover:text-white transition-colors duration-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Email Us</p>
                      <p className="text-gray-600">salstechmedia@gmail.com</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 group cursor-pointer">
                    <div className="w-12 h-12 bg-sals-light/20 rounded-full flex items-center justify-center group-hover:bg-sals-primary transition-colors duration-300 group-hover:scale-110 transform">
                      <svg
                        className="w-6 h-6 text-sals-primary group-hover:text-white transition-colors duration-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Call Us</p>
                      <p className="text-gray-600">+234 813 132 7035</p>
                      <p className="text-gray-500 text-sm">Mon-Fri, 9am-6pm</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-sals-dark to-sals-primary rounded-2xl p-8 text-white text-center transform hover:scale-105 transition-transform duration-300 shadow-xl relative overflow-hidden">
                <div className="absolute inset-0 bg-white/5 transform -skew-x-12 translate-x-full hover:translate-x-0 transition-transform duration-1000"></div>
                <div className="relative z-10">
                  <p className="text-2xl font-bold mb-2">Ready to start your project?</p>
                  <p className="mb-4 opacity-90">Get a free consultation today</p>
                  <button className="bg-white text-sals-dark px-6 py-2 rounded-lg font-semibold hover:bg-sals-light hover:text-white transition-all duration-300 transform hover:scale-105 shadow-lg">
                    Schedule Meeting
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer scrollToSection={scrollToSection} />

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scroll {
          0% {
            opacity: 1;
            transform: translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateY(10px);
          }
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0) translateX(0);
          }
          25% {
            transform: translateY(-20px) translateX(10px);
          }
          50% {
            transform: translateY(10px) translateX(-10px);
          }
          75% {
            transform: translateY(-10px) translateX(20px);
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%) rotate(45deg);
          }
          100% {
            transform: translateX(100%) rotate(45deg);
          }
        }

        @keyframes pulse-slow {
          0%,
          100% {
            opacity: 0.2;
          }
          50% {
            opacity: 0.5;
          }
        }

        .animate-slide-up {
          animation: slide-up 0.8s ease-out forwards;
          opacity: 0;
        }

        .animate-scroll {
          animation: scroll 1.5s infinite;
        }

        .animate-float {
          animation: float linear infinite;
        }

        .animate-shimmer {
          animation: shimmer 3s infinite;
        }

        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }

        .animate-bounce {
          animation: bounce 2s infinite;
        }

        @keyframes bounce {
          0%,
          100% {
            transform: translateY(-25%);
            animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
          }
          50% {
            transform: translateY(0);
            animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
          }
        }
      `}</style>
    </div>
  );
};

export default Home;